import { useMemo } from 'react';
import {
  AutoGroupColumnDef,
  ColDef,
  RowSelectionOptions,
} from 'ag-grid-community';
import DateTimeCell from './CellRenders/DateTimeCell/DateTimeCell';
import DurationCell from './CellRenders/DurationCell/DurationCell';
import FileNameCell from './CellRenders/FileNameCell/FileNameCell';
import IDCell from './CellRenders/IDCell/IDCell';
import LogDataCell from './CellRenders/LogDataCell/LogDataCell';
import OrchestrationNameCell from './CellRenders/OrchestrationNameCell/OrchestrationNameCell';
import StatusCell from './CellRenders/StatusCell/StatusCell';
import StepCell from './CellRenders/StepCell/StepCell';
import UserCell from './CellRenders/UserCell/UserCell';
import DurationFilter from './DashboardFilters/DurationFilter/DurationFilter';
import EndTimeFilter from './DashboardFilters/EndTimeFilter/EndTimeFilter';
import FileNameFilter from './DashboardFilters/FileNameFilter/FileNameFilter';
import JobIdFilter from './DashboardFilters/JobIdFilter/JobIdFilter';
import OrchestrationNameFilter from './DashboardFilters/OrchestrationNameFilter/OrchestrationNameFilter';
import StartTimeFilter from './DashboardFilters/StartTimeFilter/StartTimeFilter';
import StatusFilter from './DashboardFilters/StatusFilter/StatusFilter';
import UserFilter from './DashboardFilters/UserFilter/UserFilter';

const getRowId = (parameters: { data: { jobId: number } }) => {
  return String(parameters.data.jobId);
};

export const useGridConfiguration = () => {
  const columnDefinitions = useMemo<ColDef[]>(
    () => [
      {
        cellRenderer: OrchestrationNameCell,
        colId: 'workflowNames',
        field: 'workflowNames',
        filter: OrchestrationNameFilter,
        filterParams: {
          buttons: ['cancel', 'reset', 'apply'],
          closeOnApply: true,
        },
        headerName: 'Orchestration Name/Product Variant',
        minWidth: 370,
        width: 370,
      },
      {
        cellRenderer: FileNameCell,
        colId: 'originalFileName',
        field: 'originalFileName',
        filter: FileNameFilter,
        filterParams: {
          buttons: ['cancel', 'reset', 'apply'],
          closeOnApply: true,
        },
        headerName: 'Input File',
        minWidth: 220,
      },
      {
        cellRenderer: DateTimeCell,
        colId: 'startTime',
        field: 'startTime',
        filter: StartTimeFilter,
        filterParams: {
          buttons: ['cancel', 'reset', 'apply'],
          closeOnApply: true,
          suppressAndOrCondition: true,
        },
        headerName: 'Start Time',
        minWidth: 170,
        resizable: false,
        width: 170,
      },
      {
        cellRenderer: DateTimeCell,
        colId: 'endTime',
        field: 'endTime',
        filter: EndTimeFilter,
        filterParams: {
          buttons: ['cancel', 'reset', 'apply'],
          closeOnApply: true,
          suppressAndOrCondition: true,
        },
        headerName: 'End Time',
        minWidth: 170,
        resizable: false,
        suppressAutoSize: false,
        width: 170,
      },
      {
        cellRenderer: DurationCell,
        colId: 'duration',
        filter: DurationFilter,
        filterParams: {
          buttons: ['cancel', 'reset', 'apply'],
          closeOnApply: true,
          suppressAndOrCondition: true,
        },
        headerName: 'Duration',
        minWidth: 155,
        resizable: false,
        suppressAutoSize: false,
        valueGetter: (parameters) => {
          const startTime = parameters.data?.startTime;
          const endTime = parameters.data?.endTime;
          if (!startTime) {
            return 0;
          }
          const start = new Date(startTime).getTime();
          const end = endTime ? new Date(endTime).getTime() : Date.now();
          return end - start;
        },
        width: 155,
      },
      {
        cellRenderer: StatusCell,
        cellStyle: { textAlign: 'center' },
        colId: 'status',
        field: 'status',
        filter: StatusFilter,
        filterParams: {
          buttons: ['cancel', 'reset', 'apply'],
          closeOnApply: true,
        },

        headerName: 'Status',
        maxWidth: 125,
        minWidth: 125,
        resizable: false,
        width: 125,
      },
      {
        cellRenderer: LogDataCell,
        cellStyle: { textAlign: 'center' },
        colId: 'logData',
        field: 'logData',
        headerName: 'Log Data',
        maxWidth: 100,
        minWidth: 100,
        resizable: false,
        width: 100,
      },
      {
        cellRenderer: UserCell,
        colId: 'user',
        field: 'user',
        filter: UserFilter,
        filterParams: {
          buttons: ['cancel', 'reset', 'apply'],
          closeOnApply: true,
        },
        headerName: 'Job Started By',
        minWidth: 175,
        resizable: true,
        valueGetter: (parameters) => parameters.data?.user?.email || '',
      },
      {
        cellRenderer: StepCell,
        colId: 'steps',
        field: 'status',
        headerName: 'Step',
        minWidth: 120,
        resizable: false,
      },
    ],
    []
  );

  const defaultColumnDefinition = useMemo<ColDef>(
    () => ({
      cellStyle: { textAlign: 'left' },
      flex: 1,
      headerStyle: { textAlign: 'left' },
      resizable: true,
      sortable: false,
      suppressHeaderMenuButton: true,
    }),
    []
  );

  const autoGroupColumnDefinition = useMemo<AutoGroupColumnDef>(
    () => ({
      cellRendererParams: {
        innerRenderer: IDCell,
        suppressCount: true,
      },
      field: 'jobId',
      filter: JobIdFilter,
      filterParams: {
        buttons: ['cancel', 'reset', 'apply'],
        closeOnApply: true,
      },
      headerName: 'ID',
      headerStyle: { textAlign: 'right' },
      minWidth: 120,
      resizable: true,
      sortable: false,
      width: 120,
    }),
    []
  );

  const selectionColumnDefinition = useMemo<ColDef>(
    () => ({
      minWidth: 50,
    }),
    []
  );

  const rowSelection: RowSelectionOptions = useMemo(
    () => ({
      checkboxes: true,
      groupSelects: 'descendants',
      headerCheckbox: true,
      mode: 'multiRow',
    }),
    []
  );

  return {
    autoGroupColumnDefinition,
    columnDefinitions,
    defaultColumnDefinition,
    getRowId,
    rowSelection,
    selectionColumnDefinition,
  };
};
